import { defineStore } from 'pinia'
import { readonly, ref } from 'vue'

import api from '@/client/core/api'
import { useSystemStore } from '@/client/store/useSystemStore'
import { TCampaign } from '@/typings/api/campaigns.type'
import {
  TCampaignInfo,
  TGetInstantwinMessagesParams as Params,
  TInstantwinMessage
} from '@/typings/api/twitter_instantwin_messages.type'

export type TwitterInstantwinMessageState = {
  messages: TInstantwinMessage[]
  campaignInfo: TCampaignInfo
  total: number
  error: any
  isLoadingInstantwinMessages: boolean
  isLoadingCampaign: boolean
  campaign: TCampaign | null
  params: Params
}

const createInitialState = (): TwitterInstantwinMessageState => ({
  messages: [],
  total: 0,
  isLoadingInstantwinMessages: true,
  isLoadingCampaign: true,
  params: {
    campaignId: 0,
    sortBy: '-status',
    pagingNo: 1,
    limitCount: 25
  },
  campaign: null,
  error: null,
  campaignInfo: {
    totalApplicantCount: 0,
    totalEntryCount: 0,
    totalWinnerCount: 0,
    totalLotteryCount: 0,
    currentWinnerCount: 0
  }
})

export const useTwitterInstantwinMessageStore = defineStore('twitterInstantwinMessageStore', () => {
  const systemStore = useSystemStore()
  const initialState = createInitialState()

  const messages = ref<TwitterInstantwinMessageState['messages']>(initialState.messages)
  const total = ref<TwitterInstantwinMessageState['total']>(initialState.total)
  const isLoadingInstantwinMessages = ref<TwitterInstantwinMessageState['isLoadingInstantwinMessages']>(
    initialState.isLoadingInstantwinMessages
  )
  const isLoadingCampaign = ref<TwitterInstantwinMessageState['isLoadingCampaign']>(initialState.isLoadingCampaign)
  const params = ref<TwitterInstantwinMessageState['params']>(initialState.params)
  const campaign = ref<TwitterInstantwinMessageState['campaign']>(initialState.campaign)
  const error = ref<TwitterInstantwinMessageState['error']>(initialState.error)
  const campaignInfo = ref<TwitterInstantwinMessageState['campaignInfo']>(initialState.campaignInfo)

  const reset = async (): Promise<void> => {
    const nextState = createInitialState()
    messages.value = nextState.messages
    total.value = nextState.total
    isLoadingInstantwinMessages.value = nextState.isLoadingInstantwinMessages
    isLoadingCampaign.value = nextState.isLoadingCampaign
    params.value = nextState.params
    campaign.value = nextState.campaign
    error.value = nextState.error
    campaignInfo.value = nextState.campaignInfo
  }

  const fetchInstantwinMessages = async (): Promise<void> => {
    error.value = false
    await systemStore.setLoading(true)
    isLoadingInstantwinMessages.value = true

    const result = await api.get('/twitter_instantwin_messages', {
      params: params.value
    })

    await systemStore.setLoading(false)
    isLoadingInstantwinMessages.value = false

    if (!result.data) {
      error.value = true
      return
    }

    messages.value = result.data.messages
    total.value = result.data.total
    campaignInfo.value = result.data.campaignInfo
  }

  const setParams = (newParams: Partial<Params>): void => {
    params.value = {
      ...params.value,
      ...newParams,
      pagingNo: newParams.pagingNo || 1
    }
  }

  const fetchCampaign = async (): Promise<void> => {
    isLoadingCampaign.value = true
    const result = await api.get('/campaigns/' + params.value.campaignId)
    if (result.data) {
      campaign.value = result.data
    }

    isLoadingCampaign.value = false
  }

  return {
    messages: readonly(messages),
    total: readonly(total),
    isLoadingInstantwinMessages: readonly(isLoadingInstantwinMessages),
    isLoadingCampaign: readonly(isLoadingCampaign),
    params: readonly(params),
    campaign: readonly(campaign),
    error: readonly(error),
    campaignInfo: readonly(campaignInfo),
    reset,
    fetchInstantwinMessages,
    setParams,
    fetchCampaign
  }
})
